<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Catalogue</h1>
        <p class="page-subtitle">{{ products.length }} produit{{ products.length > 1 ? 's' : '' }} — Éditez directement dans le tableau</p>
      </div>
      <div class="d-flex align-center" style="gap: 8px;">
        <span v-if="dirtyCount > 0" class="dirty-badge">{{ dirtyCount }} modifié{{ dirtyCount > 1 ? 's' : '' }}</span>
        <v-btn
          color="primary"
          depressed
          @click="save"
          :loading="save_loading"
          :disabled="save_loading || dirtyCount === 0"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Sauvegarder
        </v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Search -->
      <div class="d-flex justify-end mb-3">
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <!-- Spreadsheet -->
      <div class="spreadsheet-card">
        <div class="spreadsheet-wrapper">
          <table class="spreadsheet" @keydown="handleKeydown">
            <thead>
              <tr>
                <th class="col-name">NOM</th>
                <th class="col-category">CATÉGORIE</th>
                <th class="col-barcode">CODE BARRE</th>
                <th class="col-number">COÛT <span class="currency-tag">{{ currencyLabel }}</span></th>
                <th class="col-number">PRIX <span class="currency-tag">{{ currencyLabel }}</span></th>
                <th class="col-number" v-if="hasMinPrice">PRIX MIN <span class="currency-tag">{{ currencyLabel }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(product, rowIdx) in filteredProducts"
                :key="product.id"
                :class="{ 'row-dirty': product._dirty }"
              >
                <td class="col-name">
                  <input
                    type="text"
                    v-model="product.name"
                    @input="markDirty(product)"
                    @focus="setActive(rowIdx, 0)"
                    :ref="'cell-' + rowIdx + '-0'"
                    class="cell-input cell-text"
                  />
                </td>
                <td class="col-category">
                  <select
                    v-model="product.category_id"
                    @change="markDirty(product)"
                    @focus="setActive(rowIdx, 1)"
                    :ref="'cell-' + rowIdx + '-1'"
                    class="cell-input cell-select"
                  >
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </td>
                <td class="col-barcode">
                  <input
                    type="text"
                    v-model="product.barcode"
                    @input="markDirty(product)"
                    @focus="setActive(rowIdx, 2)"
                    :ref="'cell-' + rowIdx + '-2'"
                    class="cell-input cell-text"
                  />
                </td>
                <td class="col-number">
                  <input
                    type="number"
                    v-model.number="product.cost"
                    @input="markDirty(product)"
                    @focus="setActive(rowIdx, 3)"
                    :ref="'cell-' + rowIdx + '-3'"
                    class="cell-input cell-number"
                    step="0.01"
                  />
                </td>
                <td class="col-number">
                  <input
                    type="number"
                    v-model.number="product.price"
                    @input="markDirty(product)"
                    @focus="setActive(rowIdx, 4)"
                    :ref="'cell-' + rowIdx + '-4'"
                    class="cell-input cell-number"
                    step="0.01"
                  />
                </td>
                <td class="col-number" v-if="hasMinPrice">
                  <input
                    type="number"
                    v-model.number="product.min_price"
                    @input="markDirty(product)"
                    @focus="setActive(rowIdx, 5)"
                    :ref="'cell-' + rowIdx + '-5'"
                    class="cell-input cell-number"
                    step="0.01"
                  />
                </td>
              </tr>
              <tr v-if="filteredProducts.length === 0">
                <td :colspan="hasMinPrice ? 6 : 5" class="empty-row">Aucun produit trouvé</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'CatalogView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    categories: [],
    store: {},
    products: [],
    rate: 1,
    rate_id: 1,
    hasMinPrice: false,
    search: '',
    save_loading: false,
    enable: false,
    business_id: '',
    activeRow: 0,
    activeCol: 0,
    items: [{ text: 'Catalogue', disabled: false, href: '/catalog' }],
  }),

  computed: {
    currencyLabel() {
      return this.rate_id == 1 ? 'HTG' : 'USD'
    },
    filteredProducts() {
      if (!this.search.trim()) return this.products
      var q = this.search.toLowerCase()
      return this.products.filter(function (p) {
        return (p.name && p.name.toLowerCase().indexOf(q) !== -1) ||
               (p.barcode && p.barcode.toLowerCase().indexOf(q) !== -1)
      })
    },
    dirtyCount() {
      return this.products.filter(function (p) { return p._dirty }).length
    },
    colCount() {
      return this.hasMinPrice ? 6 : 5
    },
  },

  methods: {
    markDirty(product) {
      this.$set(product, '_dirty', true)
    },

    setActive(row, col) {
      this.activeRow = row
      this.activeCol = col
    },

    focusCell(row, col) {
      // Clamp
      if (row < 0) row = 0
      if (row >= this.filteredProducts.length) row = this.filteredProducts.length - 1
      if (col < 0) col = 0
      if (col >= this.colCount) col = this.colCount - 1

      // Skip column 5 if no min price
      if (!this.hasMinPrice && col === 5) col = 4

      var ref = this.$refs['cell-' + row + '-' + col]
      if (ref) {
        var el = Array.isArray(ref) ? ref[0] : ref
        if (el) {
          el.focus()
          if (el.select) el.select()
        }
      }
      this.activeRow = row
      this.activeCol = col
    },

    handleKeydown(e) {
      var row = this.activeRow
      var col = this.activeCol

      if (e.key === 'Tab') {
        e.preventDefault()
        if (e.shiftKey) {
          // Shift+Tab: go left or wrap to previous row
          if (col > 0) { this.focusCell(row, col - 1) }
          else if (row > 0) { this.focusCell(row - 1, this.colCount - 1) }
        } else {
          // Tab: go right or wrap to next row
          if (col < this.colCount - 1) { this.focusCell(row, col + 1) }
          else if (row < this.filteredProducts.length - 1) { this.focusCell(row + 1, 0) }
        }
      } else if (e.key === 'Enter') {
        e.preventDefault()
        // Enter: go to next row, same column
        if (row < this.filteredProducts.length - 1) {
          this.focusCell(row + 1, col)
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.focusCell(row + 1, col)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.focusCell(row - 1, col)
      } else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length) {
        // Only move right if cursor is at end
        e.preventDefault()
        this.focusCell(row, col + 1)
      } else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
        // Only move left if cursor is at start
        e.preventDefault()
        this.focusCell(row, col - 1)
      }
    },

    save() {
      var self = this
      this.save_loading = true
      // Set status=2 for all products (signals update)
      var payload = this.products.map(function (p) {
        return { id: p.id, price: p.price, cost: p.cost, status: 2, category_id: p.category_id, name: p.name, barcode: p.barcode, min_price: p.min_price }
      })
      this.$axios.post('/store-products/modify?data=' + encodeURIComponent(JSON.stringify(payload)))
        .then(function () {
          self.save_loading = false
          // Clear dirty flags
          self.products.forEach(function (p) { self.$set(p, '_dirty', false) })
          self.flashMessage.success({ title: 'Succès', message: 'Catalogue sauvegardé' })
        })
        .catch(function (error) {
          self.save_loading = false
          alert('Erreur: ' + error)
        })
    },

    load() {
      var self = this
      this.$axios.get('/products?orderasc=name')
        .then(function (response) {
          self.products = response.data.map(function (p) {
            return {
              id: p.id,
              name: p.name,
              barcode: p.barcode || '',
              category_id: p.category_id,
              cost: p.cost,
              price: p.price,
              min_price: p.min_price || '',
              _dirty: false,
            }
          })
        })
    },

    getCategories() {
      var self = this
      this.$axios.get('/categories?orderasc=name')
        .then(function (response) { self.categories = response.data })
    },

    getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      this.$axios.get('/stores?orderasc=name&include=store_rates&business_id=' + this.business_id)
        .then(function (response) {
          if (response.data.length > 0) {
            self.store = response.data[0]
            self.rate_id = self.store.rate_id
            self.hasMinPrice = !!self.store.min_price
            for (var j = (self.store.store_rates || []).length - 1; j >= 0; j--) {
              if (self.store.store_rates[j].rate_id == 2) self.rate = self.store.store_rates[j].sales_rate
            }
          }
          self.enable = true
        })
    },
  },

  mounted() {
    this.getStores()
    this.getCategories()
    this.load()
  },
}
</script>

<style scoped>
.search-input {
  width: 260px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  color: #1a2332;
  background: #fff;
}
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.search-input::placeholder { color: #94a3b8; }

.dirty-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #f68d3b;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

/* Spreadsheet */
.spreadsheet-card {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.spreadsheet-wrapper {
  overflow: auto;
  max-height: calc(100vh - 200px);
}

.spreadsheet {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header */
.spreadsheet thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f1f5f9;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  padding: 10px 8px;
  border-bottom: 2px solid #cbd5e1;
  border-right: 1px solid #e2e8f0;
  text-align: center;
  white-space: nowrap;
  user-select: none;
}

.spreadsheet thead th:last-child { border-right: none; }

.currency-tag {
  font-size: 0.5625rem;
  font-weight: 600;
  color: #16a34a;
  margin-left: 2px;
}

/* Column widths */
.col-name { width: 30%; text-align: left !important; }
.col-category { width: 18%; }
.col-barcode { width: 15%; }
.col-number { width: 12%; }

/* Cells */
.spreadsheet tbody td {
  padding: 0;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  height: 38px;
}

.spreadsheet tbody td:last-child { border-right: none; }

/* Input cells */
.cell-input {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 10px;
  font-size: 0.8125rem;
  font-family: inherit;
  color: #1e293b;
  background: transparent;
  box-sizing: border-box;
}

.cell-input:focus {
  background: #eff6ff;
  box-shadow: inset 0 0 0 2px #3b82f6;
}

.cell-text { text-align: left; }
.cell-number { text-align: right; font-variant-numeric: tabular-nums; }

.cell-select {
  text-align: center;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 22px;
}

.cell-select:focus {
  background-color: #eff6ff;
  box-shadow: inset 0 0 0 2px #3b82f6;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%233b82f6'/%3E%3C/svg%3E");
}

/* Remove number input spinners */
.cell-number::-webkit-inner-spin-button,
.cell-number::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.cell-number { -moz-appearance: textfield; }

/* Dirty row highlight */
.row-dirty {
  background: #fffbeb !important;
}

.row-dirty td {
  border-bottom-color: #fde68a !important;
}

/* Hover */
.spreadsheet tbody tr:hover td {
  background: #f8fafc;
}

.row-dirty:hover td {
  background: #fef9c3 !important;
}

/* Empty state */
.empty-row {
  text-align: center;
  padding: 40px 16px !important;
  color: #94a3b8;
  font-size: 0.875rem;
}
</style>
